// src/audioKit.ts — FINAL HARDENED v2 (adds tail guard preemption)
// Mono playback with preemption, flow-safe play (never deadlocks), cooldown,
// retries, cache-first preloading, background warming, LRU memory, in-flight
// de-duplication, optional transform (downmix/resample), telemetry (Amplitude-ready),
// and Sentry hooks. Tail guard: optional non-preempt window to keep chime tails smooth.

import { idle } from "./utils/idle";
import { withRetry } from "./utils/retry";
import { FetchError, DecodeError } from "./utils/errors";
import type {
    AudioId,
    AudioKit,
    AudioKitInit,
    BackgroundWarmOpts,
    PlayOpts,
    PreloadOpts,
    SoundItem,
    Logger,
    TelemetrySink,
    SentryLike,
    RetryPolicy,
    FlowGuardOpts,
    PlayResult,
    PlayResultStatus,
    PlayMode,
} from "./types";

export function defaultPickFormat(base: string): string {
    const a = document.createElement("audio");
    if (a.canPlayType('audio/ogg; codecs="opus"')) return `${base}.ogg`;
    if (a.canPlayType('audio/webm; codecs="opus"')) return `${base}.webm`;
    if (a.canPlayType('audio/mp4; codecs="mp4a.40.2"')) return `${base}.m4a`;
    return `${base}.mp3`;
}

const defaultLogger: Logger = {
    debug: (m, c) => console.debug("[audio-kit]", m, c ?? ""),
    info: (m, c) => console.info("[audio-kit]", m, c ?? ""),
    warn: (m, c) => console.warn("[audio-kit]", m, c ?? ""),
    error: (m, c) => console.error("[audio-kit]", m, c ?? ""),
};

const defaultRetry: RetryPolicy = {
    retries: 3,
    baseDelayMs: 300,
    maxDelayMs: 5000,
    jitter: true,
    timeoutMs: 12000,
};

export function createAudioKit(init: AudioKitInit = {}): AudioKit {
    const cacheName = init.cacheName ?? "audio-assets-v1";
    const pickFormat = init.pickFormat ?? defaultPickFormat;
    const logger = init.logger ?? defaultLogger;
    const telemetry = init.telemetry ?? ({ event: () => {} } as TelemetrySink);
    const sentry: SentryLike | undefined = init.sentry;
    const retry = init.retry ?? defaultRetry;
    const playMode: PlayMode = init.playMode ?? "mono";
    const cooldown = init.cooldown ?? {};
    const globalCooldown = cooldown.globalMinIntervalMs ?? 80;
    const perIdCooldown = cooldown.perIdMinIntervalMs ?? {};
    const mem = init.memory ?? {
        maxDecodedBytes: 32 * 1024 * 1024,
        maxBuffers: 256,
    };
    const transform = init.transform ?? {};
    const preempt = init.preempt ?? { minTailMs: 0, dropOnTail: true };

    // Check if we're in the browser environment
    if (typeof window === "undefined") {
        // Return a mock AudioKit for SSR
        return {
            resume: async () => {},
            isReady: () => false,
            preloadCritical: async () => {},
            warmBackground: () => () => {},
            play: () => {},
            playAndWait: async () => ({
                status: "error" as const,
                id: "",
                error: new Error("SSR"),
            }),
            has: () => false,
            setMasterVolume: () => {},
            mute: () => {},
            unmute: () => {},
            stopAll: () => {},
            clear: () => {},
        };
    }

    // @ts-ignore (Safari prefix handled by union)
    const ctx = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    const masterGain = ctx.createGain();
    masterGain.gain.value = 1;
    masterGain.connect(ctx.destination);

    const store = new Map<AudioId, AudioBuffer>();
    const order: string[] = [];
    let decodedBytes = 0;
    const lastPlayedAt = new Map<string, number>();

    function sizeOf(buf: AudioBuffer) {
        return buf.length * buf.numberOfChannels * 4;
    }
    function touch(id: string) {
        const i = order.indexOf(id);
        if (i >= 0) order.splice(i, 1);
        order.push(id);
    }
    function evictIfNeeded() {
        while (
            (mem.maxDecodedBytes &&
                decodedBytes > (mem.maxDecodedBytes ?? Infinity)) ||
            (mem.maxBuffers && store.size > (mem.maxBuffers ?? Infinity))
        ) {
            const victim = order.shift();
            if (!victim) break;
            const b = store.get(victim);
            if (!b) continue;
            decodedBytes -= sizeOf(b);
            store.delete(victim);
            telemetry.event("audio.evict", { id: victim });
        }
    }
    function put(id: string, buf: AudioBuffer) {
        const sz = sizeOf(buf);
        if (store.has(id)) decodedBytes -= sizeOf(store.get(id)!);
        store.set(id, buf);
        decodedBytes += sz;
        touch(id);
        evictIfNeeded();
    }

    const inflight = new Map<string, Promise<void>>();

    async function cacheFirstBytes(url: string): Promise<ArrayBuffer> {
        return withRetry<ArrayBuffer>(
            async (_attempt, signal) => {
                if ("caches" in window) {
                    const cache = await caches.open(cacheName);
                    const req = new Request(url, { mode: "cors", signal });
                    const hit = await cache.match(req);
                    if (hit) {
                        telemetry.event("audio.cache_hit", { url });
                        return await hit.arrayBuffer();
                    }
                    const res = await fetch(req);
                    if (!res.ok)
                        throw new FetchError(
                            `HTTP ${res.status}`,
                            url,
                            res.status,
                        );
                    if (res.type === "basic" || res.type === "cors")
                        cache.put(req, res.clone()).catch(() => {});
                    return await res.arrayBuffer();
                } else {
                    const res = await fetch(url, { mode: "cors", signal });
                    if (!res.ok)
                        throw new FetchError(
                            `HTTP ${res.status}`,
                            url,
                            res.status,
                        );
                    return await res.arrayBuffer();
                }
            },
            retry,
            (attempt, err) => {
                if (err) {
                    logger.warn("fetch retry", { url, attempt, err });
                    telemetry.event("audio.fetch_retry", { url, attempt });
                }
            },
        ).catch((err) => {
            sentry?.captureException(err, {
                tags: { stage: "fetch" },
                extra: { url },
            });
            logger.error("fetch failed", { url, err });
            telemetry.event("audio.fetch_fail", { url });
            throw err;
        });
    }

    const decode = (id: string, bytes: ArrayBuffer) =>
        withRetry<AudioBuffer>(
            async () =>
                new Promise<AudioBuffer>((res, rej) => {
                    ctx.decodeAudioData(bytes.slice(0), res, () =>
                        rej(new DecodeError("decodeAudioData failed", id)),
                    );
                }),
            retry,
            (attempt, err) => {
                if (err) {
                    logger.warn("decode retry", { id, attempt, err });
                    telemetry.event("audio.decode_retry", { id, attempt });
                }
            },
        ).catch((err) => {
            sentry?.captureException(err, {
                tags: { stage: "decode" },
                extra: { id },
            });
            logger.error("decode failed", { id, err });
            telemetry.event("audio.decode_fail", { id });
            throw err;
        });

    async function transformBuffer(input: AudioBuffer) {
        const targetCh = transform.downmixToMono ? 1 : input.numberOfChannels;
        const targetSr = transform.targetSampleRate ?? input.sampleRate;
        if (
            targetCh === input.numberOfChannels &&
            targetSr === input.sampleRate
        )
            return input;
        const offline = new OfflineAudioContext(
            targetCh,
            Math.ceil(input.duration * targetSr),
            targetSr,
        );
        const src = offline.createBufferSource();
        src.buffer = input;
        src.connect(offline.destination);
        src.start();
        return await offline.startRendering();
    }

    function buildUrl(item: SoundItem): string {
        if (item.url) return item.url;
        if (!init.cdnBase || !item.base)
            throw new Error("Need url or (cdnBase+base)");
        const base = `${init.cdnBase.replace(/\/$/, "")}/${item.base}`;
        return pickFormat(base);
    }

    async function preloadOne(it: SoundItem) {
        if (inflight.has(it.id)) return inflight.get(it.id)!;
        const p = (async () => {
            const url = buildUrl(it);
            const bytes = await cacheFirstBytes(url);
            let buf = await decode(it.id, bytes);
            try {
                buf = await transformBuffer(buf);
            } catch {
                telemetry.event("audio.transform_fail", { id: it.id });
            }
            put(it.id, buf);
            telemetry.event("audio.preloaded", {
                id: it.id,
                bytes: bytes.byteLength,
                dur: buf.duration,
            });
        })().finally(() => inflight.delete(it.id));
        inflight.set(it.id, p);
        return p;
    }

    async function preloadCritical(list: SoundItem[], opts: PreloadOpts = {}) {
        const { onProgress, onError } = opts;
        const total = list.length;
        let done = 0;
        const concurrency = Math.max(1, opts.concurrency ?? 4);
        const queue = list.slice();
        const workers = Array.from({ length: concurrency }, async () => {
            while (queue.length) {
                const it = queue.shift()!;
                try {
                    await preloadOne(it);
                    onProgress?.(++done / total, it.id);
                } catch (e) {
                    onError?.(it.id, e);
                }
            }
        });
        await Promise.all(workers);
    }

    function warmBackground(list: SoundItem[], opts: BackgroundWarmOpts = {}) {
        const { onProgress, onError, isActive } = opts;
        const total = list.length;
        let i = 0,
            loaded = 0,
            canceled = false;
        const step = async (deadline: any) => {
            if (canceled) return;
            if (isActive && !isActive()) {
                idle(step);
                return;
            }
            if (deadline.timeRemaining() < 4) {
                idle(step);
                return;
            }
            if (i >= list.length) return;
            const it = list[i++];
            try {
                await preloadOne(it);
                onProgress?.(++loaded / total, it.id);
            } catch (e) {
                onError?.(it.id, e);
            }
            idle(step);
        };
        idle(step);
        return () => {
            canceled = true;
        };
    }

    function isCoolingDown(id: string): boolean {
        const now = performance.now();
        const prev = lastPlayedAt.get(id) ?? 0;
        const minGap = perIdCooldown[id] ?? globalCooldown;
        return now - prev < minGap;
    }
    function markPlayedNow(id: string) {
        lastPlayedAt.set(id, performance.now());
    }

    // --- Single Voice Manager (mono) ---
    type VoiceCtrl = {
        id: string;
        src: AudioBufferSourceNode;
        gain: GainNode;
        timer: number | null;
        resolve: (res: PlayResult) => void;
        finalized: boolean;
        endAt: number;
    };
    let voice: VoiceCtrl | null = null;

    function finalizeVoice(
        status: PlayResultStatus,
        id: string,
        error?: unknown,
    ) {
        if (!voice || voice.finalized) return;
        try {
            if (voice.timer) clearTimeout(voice.timer);
        } catch {}
        try {
            voice.src.onended = null;
        } catch {}
        try {
            voice.src.disconnect();
            voice.gain.disconnect();
        } catch {}
        voice.finalized = true;
        voice.resolve({ status, id, error });
        voice = null;
    }

    function shouldDropForTail(): boolean {
        if (!voice || !preempt.minTailMs) return false;
        const remainingMs = Math.max(0, (voice.endAt - ctx.currentTime) * 1000);
        return remainingMs <= (preempt.minTailMs || 0);
    }

    function preemptActive() {
        if (!voice) return;
        const prev = voice;
        finalizeVoice("preempted", prev.id);
        try {
            prev.src.stop(0);
        } catch {}
    }

    function startVoice(
        id: string,
        buf: AudioBuffer,
        opts: PlayOpts,
        guardMs: number,
        resolve: (r: PlayResult) => void,
    ) {
        const src = ctx.createBufferSource();
        const gain = ctx.createGain();
        src.buffer = buf;
        if (opts.rate !== undefined) src.playbackRate.value = opts.rate;
        if (opts.detune !== undefined && "detune" in src)
            (src as any).detune.value = opts.detune;
        gain.gain.value = opts.volume ?? 1;
        src.connect(gain).connect(masterGain);

        const playRate = opts.rate ?? 1;
        const durSec = buf.duration / playRate;
        const startAt = (opts.when ?? 0) + ctx.currentTime;
        const endAt = startAt + durSec;

        const ctrl: VoiceCtrl = {
            id,
            src,
            gain,
            timer: null,
            resolve,
            finalized: false,
            endAt,
        };
        src.onended = () => finalizeVoice("ok", id);
        voice = ctrl;
        ctrl.timer = window.setTimeout(
            () => finalizeVoice("timeout", id),
            guardMs,
        );
        src.start(startAt);
    }

    async function resume() {
        if (ctx.state === "suspended") await ctx.resume();
    }
    function isReady() {
        return ctx.state === "running";
    }

    function play(id: AudioId, opts: PlayOpts = {}) {
        const buf = store.get(id);
        if (!buf) return;
        if (isCoolingDown(id)) {
            cooldown.onDrop?.(id);
            telemetry.event("audio.drop_cooldown", { id });
            return;
        }
        if (playMode === "mono") {
            if (shouldDropForTail() && (preempt.dropOnTail ?? true)) {
                telemetry.event("audio.drop_tail", { id });
                return; // drop new play; keep current tail
            }
            preemptActive();
        }
        const guard = Math.max(
            3000,
            (buf.duration / (opts.rate ?? 1)) * 1000 + 1500,
        );
        startVoice(id, buf, opts, guard, () => {});
        markPlayedNow(id);
        telemetry.event("audio.play_ok", { id, mode: "fire" });
    }

    async function playAndWait(
        id: AudioId,
        opts: PlayOpts & FlowGuardOpts = {},
    ): Promise<PlayResult> {
        const buf = store.get(id);
        if (!buf) {
            telemetry.event("audio.play_missing", { id });
            return { status: "missing", id };
        }
        if (isCoolingDown(id)) {
            cooldown.onDrop?.(id);
            telemetry.event("audio.drop_cooldown", { id });
            return { status: "dropped", id };
        }
        if (ctx.state !== "running") {
            try {
                await ctx.resume();
            } catch (e) {
                telemetry.event("audio.play_error", { id });
                return { status: "error", id, error: e };
            }
        }
        if (playMode === "mono") {
            if (shouldDropForTail() && (preempt.dropOnTail ?? true)) {
                telemetry.event("audio.drop_tail", { id });
                return { status: "dropped", id };
            }
            preemptActive();
        }

        const expectedMs = (buf.duration / (opts.rate ?? 1)) * 1000;
        const guard = Math.max(0, opts.maxMs ?? expectedMs + 1500);
        markPlayedNow(id);

        return new Promise<PlayResult>((resolve) => {
            startVoice(id, buf, opts, guard, (res) => {
                telemetry.event(`audio.play_${res.status}`, { id });
                resolve(res);
            });
        });
    }

    function setMasterVolume(v: number) {
        masterGain.gain.value = Math.max(0, Math.min(1, v));
    }
    function mute() {
        setMasterVolume(0);
    }
    function unmute() {
        setMasterVolume(1);
    }
    function has(id: AudioId) {
        return store.has(id);
    }
    function stopAll() {
        if (voice) preemptActive();
    }
    function clear() {
        stopAll();
        store.clear();
        decodedBytes = 0;
        order.splice(0);
    }

    document.addEventListener("visibilitychange", () => {
        if (
            document.visibilityState === "visible" &&
            ctx.state === "suspended"
        ) {
            ctx.resume().catch(() => {});
        }
    });

    return {
        resume,
        isReady,
        preloadCritical,
        warmBackground,
        play,
        playAndWait,
        has,
        setMasterVolume,
        mute,
        unmute,
        stopAll,
        clear,
    };
}
